/**
 * Confirmation Dialog for Plan Deletion
 */

import styled from 'styled-components';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { AlertTriangle } from 'lucide-react';
import { Plan } from '../../services/api/planService';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  plan: Plan | null;
  isLoading: boolean;
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const WarningBox = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.status.warningLight};
  border: 1px solid ${({ theme }) => theme.colors.status.warning.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const WarningIcon = styled.div`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.status.warning.text};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const WarningContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const WarningTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const WarningText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

const PlanNameHighlight = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  plan,
  isLoading,
}: ConfirmationDialogProps) => {
  if (!plan) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Plan"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} isLoading={isLoading}>
            Delete Plan
          </Button>
        </>
      }
    >
      <Content>
        <WarningBox>
          <WarningIcon>
            <AlertTriangle />
          </WarningIcon>
          <WarningContent>
            <WarningTitle>Are you sure?</WarningTitle>
            <WarningText>
              You are about to delete the plan{' '}
              <PlanNameHighlight>&quot;{plan.name}&quot;</PlanNameHighlight>.
            </WarningText>
            {plan.organizationCount !== undefined && plan.organizationCount > 0 && (
              <WarningText>
                This plan is currently assigned to{' '}
                <PlanNameHighlight>
                  {plan.organizationCount} organization{plan.organizationCount !== 1 ? 's' : ''}
                </PlanNameHighlight>
                . Deleting it will deactivate the plan — existing organizations will not lose
                access immediately, but the plan will no longer be assignable.
              </WarningText>
            )}
            {(plan.organizationCount === undefined || plan.organizationCount === 0) && (
              <WarningText>This plan has no organizations assigned to it.</WarningText>
            )}
          </WarningContent>
        </WarningBox>
      </Content>
    </Modal>
  );
};

export default ConfirmationDialog;
